import * as Sentry from '@sentry/react'
// eslint-disable-next-line import/named
import { Formik, FormikHelpers } from 'formik'
import * as React from 'react'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import FormikTextField from '@/components/formik/FormikTextField'
import Loading from '@/components/Loading'
import { useAddStripeIntegrations } from '@/queries/integrations/stripe/useAddStripeIntegration'
import { useDeleteStripeIntegrations } from '@/queries/integrations/stripe/useDeleteStripeIntegration'
import { useLoadStripeIntegrations } from '@/queries/integrations/stripe/useLoadStripeIntegration'
import { useUpdateStripeIntegrations } from '@/queries/integrations/stripe/useUpdateStripeIntegration'
import { CreateUpdateStripeIntegrationProps } from '@/types/stripe-integration'
import Banner from '@/ui/atoms/Banner'
import Button from '@/ui/atoms/Button'
import Flex from '@/ui/atoms/Flex'
import Text from '@/ui/atoms/Text'
import Page from '@/ui/organisms/Page'
import { ZentakeContext } from '@/utils/ZentakeContext'

const schema = Yup.object().shape({
  api_secret_key: Yup.string().nullable(),
  api_publishable_key: Yup.string().nullable(),
})

function StripeIntegrationSettings(): React.ReactNode {
  const { setAlertConfig } = React.useContext(ZentakeContext)
  const { data, isLoading } = useLoadStripeIntegrations()
  const { addStripeIntegration, isLoading: isAddLoading } =
    useAddStripeIntegrations()
  const { updateStripeIntegration, isLoading: isUpdateLoading } =
    useUpdateStripeIntegrations()
  const { deleteStripeIntegration, isLoading: isDeleteLoading } =
    useDeleteStripeIntegrations()
  const [isEditing, setIsEditing] = React.useState(false)

  const handleSubmit = async (
    values: CreateUpdateStripeIntegrationProps,
    {
      resetForm,
    }: FormikHelpers<{
      api_secret_key: string
      api_publishable_key: string
    }>
  ) => {
    if (values.api_secret_key === '') {
      delete values.api_secret_key
    }

    try {
      let response = undefined
      if (data) {
        response = await updateStripeIntegration(data.id, values)
      } else {
        response = await addStripeIntegration(values)
      }
      if (response.account_connection_status === 'CONNECTED') {
        toast.success('Stripe integration updated successfully')
      } else {
        toast.warning('Check your API keys and try again.')
      }
      setIsEditing(false)
      resetForm()
    } catch (e) {
      Sentry.captureException(e)
      toast.error('Something went wrong...')
    }
  }

  const showDisconnectConfirmation = React.useCallback(
    async () =>
      new Promise((resolve) =>
        setAlertConfig({
          title:
            'Are you sure you want to permanently disconnect this Stripe integration?',
          description:
            'Note: Any existing forms that use this integration will no longer be able to collect credit card.',
          buttons: [
            {
              title: 'Disconnect',
              style: 'primary',
              danger: true,
              onPress: () => resolve(true),
            },
            {
              title: 'Cancel',
              onPress: () => resolve(false),
            },
          ],
          canDismiss: false,
        })
      ),
    [setAlertConfig]
  )

  const disconnectStripe = async (resetForm: () => void) => {
    if (data) {
      try {
        await deleteStripeIntegration(data.id)
        toast.success('Stripe integration disconnected successfully')
        resetForm()
      } catch (e) {
        Sentry.captureException(e)
        toast.error('Something went wrong...')
      }
    }
  }

  if (isLoading) {
    return (
      <Page withBackButton title="Stripe Integration Settings">
        <div className="flex flex-col items-center">
          <Loading />
        </div>
      </Page>
    )
  }

  return (
    <Page centerTitle withBackButton title="Stripe Integration Settings">
      <Flex $flexDirection="col" className="rounded-lg bg-white p-4 shadow-md">
        {data?.account_connection_status === 'CONNECTED' &&
          data?.is_test_mode && (
            <Flex
              $flexDirection="col"
              $verticalSpacing="medium"
              align="center"
              justify="center"
            >
              <Banner $warning>
                <Text>
                  <Text $semibold>You are using a Test Mode API Key.</Text>
                </Text>
              </Banner>
            </Flex>
          )}

        <Text $bold className="mb-3">
          Status:
          {data?.account_connection_status === 'CONNECTED' ? (
            <Text $green $semibold>
              Connected
            </Text>
          ) : data?.account_connection_status === 'DISCONNECTED' ? (
            <Text $red $semibold>
              Disconnected
            </Text>
          ) : (
            <Text $red $semibold>
              Error Connecting
            </Text>
          )}
        </Text>

        <Formik
          enableReinitialize
          initialValues={{
            api_secret_key: isEditing || !data ? '' : '****hidden****',
            api_publishable_key: data?.api_publishable_key || '',
          }}
          onSubmit={handleSubmit}
          validationSchema={schema}
        >
          {({ handleSubmit, resetForm, touched }) => (
            <form className="w-full" onSubmit={handleSubmit}>
              <Flex $flexDirection="col" $verticalSpacing="medium">
                <Flex $flexDirection="col" $verticalSpacing="medium">
                  <FormikTextField
                    disabled={!isEditing}
                    label="API Publishable Key"
                    name="api_publishable_key"
                  />
                  <FormikTextField
                    disabled={!isEditing}
                    label="API Secret Key"
                    name="api_secret_key"
                  />
                </Flex>

                {data?.account_connection_status === 'CONNECTED' &&
                  data?.is_restricted && (
                    <Flex
                      $flexDirection="col"
                      $verticalSpacing="medium"
                      align="center"
                      className="mb-4"
                      justify="center"
                    >
                      <Banner $warning>
                        <Text>
                          <Text $semibold>
                            When using Restricted API Keys, ensure the following
                            permissions are enabled:
                          </Text>
                          <ul className="mt-2 text-left">
                            <li>
                              <span className="font-medium">Customer:</span>{' '}
                              Write access
                            </li>
                            <li>
                              <span className="font-medium">
                                Payment Method:
                              </span>{' '}
                              Write access
                            </li>
                          </ul>
                        </Text>
                      </Banner>
                    </Flex>
                  )}

                <Flex $horizontalSpacing="large" justify="end">
                  {isEditing && (
                    <Button
                      className="px-4"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                  )}
                  {!isEditing && data && (
                    <Button
                      $danger
                      loading={isLoading || isDeleteLoading}
                      onClick={async () => {
                        const confirm = await showDisconnectConfirmation()
                        if (confirm) {
                          await disconnectStripe(resetForm)
                        }
                      }}
                    >
                      Disconnect
                    </Button>
                  )}
                  {isEditing && (
                    <Button
                      $primary
                      className="px-4"
                      disabled={
                        isLoading ||
                        isUpdateLoading ||
                        isAddLoading ||
                        (!touched?.api_publishable_key &&
                          !touched?.api_secret_key)
                      }
                      loading={isLoading || isUpdateLoading || isAddLoading}
                      type="submit"
                    >
                      Save
                    </Button>
                  )}
                  {!isEditing && (
                    <Button
                      $primary
                      className="px-4"
                      onClick={() => setIsEditing(true)}
                    >
                      {data ? 'Edit' : 'Connect'}
                    </Button>
                  )}
                </Flex>
              </Flex>
            </form>
          )}
        </Formik>
      </Flex>
    </Page>
  )
}

export default StripeIntegrationSettings
